import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function kindLabel(kind, t) {
  const v = t(`proposals.kinds.${kind}`);
  return v !== `proposals.kinds.${kind}` ? v : kind;
}

function ApplyModal({ proposal, onClose, onApplied, t }) {
  const payload = proposal.payload || {};
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  // Local editable state per kind
  const [metricName, setMetricName] = useState(payload.metric_name || "");
  const [unit, setUnit] = useState(payload.unit || "");
  const [newValue, setNewValue] = useState(
    payload.delta_value !== undefined && payload.delta_value !== null ? String(payload.delta_value) : "",
  );
  const [noteText, setNoteText] = useState(payload.observation || "");
  const [tier, setTier] = useState("month_1");
  const [newText, setNewText] = useState(
    [payload.item, payload.target, payload.note].filter(Boolean).join(" — "),
  );
  const [mode, setMode] = useState("append");
  const [dTitle, setDTitle] = useState(payload.title || "");
  const [dOwner, setDOwner] = useState(payload.owner || "");
  const [dContext, setDContext] = useState(payload.context || "");

  const submit = async () => {
    setBusy(true);
    setErr("");
    let overrides = {};
    if (proposal.kind === "metric_delta") {
      overrides = { metric_name: metricName, unit, new_value: newValue };
    } else if (proposal.kind === "kpi_note") {
      overrides = { metric_name: metricName, note_text: noteText };
    } else if (proposal.kind === "roadmap_change") {
      overrides = { tier, new_text: newText, mode };
    } else if (proposal.kind === "decision") {
      overrides = { title: dTitle, owner: dOwner, context: dContext };
    }
    try {
      const r = await api.post(`/journal/proposals/${proposal.id}/apply`, { overrides });
      setOk(true);
      setTimeout(() => onApplied(proposal, r.data), 400);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("proposals.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tb-modal-backdrop" role="dialog" aria-modal="true" data-testid="apply-modal">
      <div className="tb-modal">
        <header className="tb-modal-head">
          <h3>{t("memoryApply.title")}</h3>
          <button
            type="button"
            className="tb-icon-btn"
            onClick={onClose}
            aria-label="close"
            data-testid="apply-modal-close"
          >
            ×
          </button>
        </header>
        <p className="tb-muted">{t("memoryApply.subtitle")}</p>
        <div className="tb-modal-body">
          <div className="tb-modal-summary">
            <strong>{kindLabel(proposal.kind, t)}</strong>
            <span> · </span>
            <span>{proposal.summary || "—"}</span>
          </div>

          {proposal.kind === "metric_delta" && (
            <div className="tb-form-grid">
              <label>
                {t("memoryApply.metricName")}
                <input
                  value={metricName}
                  onChange={(e) => setMetricName(e.target.value)}
                  data-testid="apply-metric-name"
                />
              </label>
              <label>
                {t("memoryApply.newValue")}
                <input
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  data-testid="apply-metric-value"
                />
              </label>
              <label>
                {t("memoryApply.unit")}
                <input
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  data-testid="apply-metric-unit"
                />
              </label>
            </div>
          )}

          {proposal.kind === "kpi_note" && (
            <div className="tb-form-grid">
              <label>
                {t("memoryApply.metricName")}
                <input
                  value={metricName}
                  onChange={(e) => setMetricName(e.target.value)}
                  data-testid="apply-kpi-metric"
                />
              </label>
              <label className="tb-form-grid-wide">
                {t("memoryApply.noteText")}
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  data-testid="apply-kpi-note"
                />
              </label>
            </div>
          )}

          {proposal.kind === "roadmap_change" && (
            <div className="tb-form-grid">
              <label>
                {t("memoryApply.tier")}
                <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="apply-roadmap-tier">
                  <option value="north_star">{t("memoryApply.tiers.north_star")}</option>
                  <option value="year_10">{t("memoryApply.tiers.year_10")}</option>
                  <option value="year_5">{t("memoryApply.tiers.year_5")}</option>
                  <option value="year_2">{t("memoryApply.tiers.year_2")}</option>
                  <option value="year_1">{t("memoryApply.tiers.year_1")}</option>
                  <option value="quarter_0">{t("memoryApply.tiers.quarter_0")}</option>
                  <option value="quarter_1">{t("memoryApply.tiers.quarter_1")}</option>
                  <option value="quarter_2">{t("memoryApply.tiers.quarter_2")}</option>
                  <option value="quarter_3">{t("memoryApply.tiers.quarter_3")}</option>
                  <option value="month_1">{t("memoryApply.tiers.month_1")}</option>
                  <option value="week_2">{t("memoryApply.tiers.week_2")}</option>
                </select>
              </label>
              <label>
                {t("memoryApply.mode")}
                <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="apply-roadmap-mode">
                  <option value="append">{t("memoryApply.modeAppend")}</option>
                  <option value="replace">{t("memoryApply.modeReplace")}</option>
                </select>
              </label>
              <label className="tb-form-grid-wide">
                {t("memoryApply.newText")}
                <textarea
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  rows={3}
                  data-testid="apply-roadmap-text"
                />
              </label>
            </div>
          )}

          {proposal.kind === "decision" && (
            <div className="tb-form-grid">
              <label>
                {t("memoryApply.decisionTitle")}
                <input
                  value={dTitle}
                  onChange={(e) => setDTitle(e.target.value)}
                  data-testid="apply-decision-title"
                />
              </label>
              <label>
                {t("memoryApply.decisionOwner")}
                <input
                  value={dOwner}
                  onChange={(e) => setDOwner(e.target.value)}
                  data-testid="apply-decision-owner"
                />
              </label>
              <label className="tb-form-grid-wide">
                {t("memoryApply.decisionContext")}
                <textarea
                  value={dContext}
                  onChange={(e) => setDContext(e.target.value)}
                  rows={3}
                  data-testid="apply-decision-context"
                />
              </label>
            </div>
          )}

          {err && <div className="tb-error">{err}</div>}
          {ok && <div className="tb-success" data-testid="apply-success">{t("memoryApply.applied")}</div>}
        </div>
        <footer className="tb-modal-foot">
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="apply-cancel"
          >
            {t("memoryApply.cancel")}
          </button>
          <button
            type="button"
            className="tb-primary-btn"
            onClick={submit}
            disabled={busy || ok}
            data-testid="apply-confirm"
          >
            {busy ? t("memoryApply.applying") : t("memoryApply.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ProposalCard({ p, onOpen, onSkip, t }) {
  const payload = p.payload || {};
  return (
    <article className="tb-journal-entry" data-testid={`proposal-${p.id}`}>
      <div className="tb-journal-entry-head">
        <span className="tb-proposal-kind" data-testid={`proposal-kind-${p.id}`}>
          {kindLabel(p.kind, t)}
        </span>
        <div className="tb-journal-meta" style={{ flex: 1 }}>
          <strong>{p.summary || "—"}</strong>
        </div>
      </div>
      <div className="tb-journal-entry-body">
        {p.kind === "metric_delta" && (
          <p>
            <strong>{payload.metric_name || "?"}</strong>{" "}
            {payload.direction === "up" ? "↑" : payload.direction === "down" ? "↓" : "·"}{" "}
            {payload.delta_value} {payload.unit}
          </p>
        )}
        {p.kind === "decision" && (
          <>
            <p><strong>{payload.title || "?"}</strong></p>
            {payload.owner && <p className="tb-muted">Owner: {payload.owner}</p>}
            {payload.context && <p>{payload.context}</p>}
          </>
        )}
        {p.kind === "roadmap_change" && (
          <>
            <p><strong>{payload.item || "?"}</strong> → <em>{payload.target || "?"}</em></p>
            {payload.note && <p>{payload.note}</p>}
          </>
        )}
        {p.kind === "kpi_note" && (
          <>
            <p><strong>{payload.metric_name || "?"}</strong></p>
            <p>{payload.observation || ""}</p>
          </>
        )}
      </div>
      <div className="tb-journal-entry-foot">
        <button
          type="button"
          className="tb-ghost-btn tb-mini"
          onClick={() => onSkip(p)}
          data-testid={`proposal-skip-${p.id}`}
        >
          {t("proposals.skip")}
        </button>
        <button
          type="button"
          className="tb-primary-btn tb-mini"
          onClick={() => onOpen(p)}
          data-testid={`proposal-apply-${p.id}`}
        >
          {t("proposals.apply")}
        </button>
      </div>
    </article>
  );
}

export default function Proposals() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);

  const canView = user && (user.role === "admin" || user.features?.journal_enabled === true);

  const load = () => {
    api.get("/journal/proposals")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiErrorDetail(e) || t("proposals.errors.generic")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]);

  const skip = async (p) => {
    try {
      await api.post(`/journal/proposals/${p.id}/skip`);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("proposals.errors.generic"));
    }
  };

  const openApply = (p) => setModal(p);
  const closeApply = () => setModal(null);
  const applied = (p /* , result */) => {
    setItems((prev) => prev.filter((x) => x.id !== p.id));
    setModal(null);
  };

  if (!canView) {
    return (
      <div className="tb-page">
        <h1>{t("proposals.title")}</h1>
        <div className="tb-card"><p className="tb-muted">{t("proposals.emptyForRole")}</p></div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-journal-page" data-testid="proposals-page">
      <header className="tb-journal-header">
        <div>
          <h1>{t("proposals.title")}</h1>
          <p className="tb-muted">{t("proposals.subtitle")}</p>
        </div>
      </header>
      {err && <div className="tb-error">{err}</div>}
      {loading ? (
        <p className="tb-muted">{t("proposals.loading")}</p>
      ) : items.length === 0 ? (
        <div className="tb-card"><p className="tb-muted">{t("proposals.empty")}</p></div>
      ) : (
        <div className="tb-journal-entries">
          {items.map((p) => (
            <ProposalCard key={p.id} p={p} onOpen={openApply} onSkip={skip} t={t} />
          ))}
        </div>
      )}
      {modal && (
        <ApplyModal
          proposal={modal}
          onClose={closeApply}
          onApplied={applied}
          t={t}
        />
      )}
    </div>
  );
}
